import { School, SearchMatchType } from '@aibees/elephant/types';
import { IconType } from '@aibees/shared/components';
import { capitalize } from '@aibees/shared/utils';
import {
  educationSearchLabelParser,
  generateEducationFilterYearOptions,
  getDomainIfExist,
} from '../components/filter-tags/utils';
import {
  ClassificationGroups,
  ClassificationType,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { listsResponseSchoolMapper } from '../utils';

export default function educationFilterConfig(): SearchFilterBase {
  const schoolFilter: SearchFilter = {
    name: 'Academics',
    type: SearchFilterType.NONE,
    icon: undefined,
    index: 0,
    key: 'school_university',
    field: 'school_university',
    metadata: { color: 'primary' },
    config: { labelKey: 'label', valueKey: 'value' },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'School/University',
        },
      },
    },
    open: true,
    sections: { main: 3 },
    layer: 4,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        field: 'include_education_school',
        key: 'include_education_school',
        layer: 4,
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          preset: 'education',
          placeholder: 'Include School / University',
          autocomplete: {
            transformSearchValue: getDomainIfExist,
            recent: false,
            multiple: true,
            checkboxPosition: 'right',
            avartarFallbackIcon: 'business',
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: School[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseSchoolMapper(data, icon, delimiter),
          tags: { labelFormatter: educationSearchLabelParser },
        },
        searchKey: 'search',
        config: { labelKey: 'label', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=school&page=0&size=10`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        key: 'exclude_education_school',
        field: 'exclude_education_school',
        layer: 4,
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Exclude School / University',
          autocomplete: {
            transformSearchValue: getDomainIfExist,
            recent: false,
            multiple: true,
            checkboxPosition: 'right',
            avartarFallbackIcon: 'business',
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: School[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseSchoolMapper(data, icon, delimiter),
          tags: { labelFormatter: educationSearchLabelParser },
        },
        searchKey: 'search',
        config: { labelKey: 'label', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=school&page=0&size=10`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        field: 'include_all_of_education_school',
        key: 'include_all_of_education_school',
        layer: 4,
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Include School / University (AND) Search',
          autocomplete: {
            transformSearchValue: getDomainIfExist,
            recent: false,
            multiple: true,
            checkboxPosition: 'right',
            avartarFallbackIcon: 'business',
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: School[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseSchoolMapper(data, icon, delimiter),
          tags: { labelFormatter: educationSearchLabelParser },
        },
        searchKey: 'search',
        config: { labelKey: 'label', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=school&page=0&size=10`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
    ],
  };

  const degreeFilter: SearchFilter = {
    name: 'Degree',
    type: SearchFilterType.NONE,
    icon: undefined,
    index: 1,
    key: 'degree',
    field: 'degree',
    metadata: { color: 'primary' },
    config: { labelKey: 'label', valueKey: 'value' },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Degree',
        },
      },
    },
    open: false,
    sections: { main: 3 },
    layer: 4,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        exactMatch: {
          enabled: true,
          default: SearchMatchType.SMART,
        },
        field: 'include_education_degree',
        key: 'include_education_degree',
        layer: 4,
        metadata: {
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Include Degree',
          autocomplete: {
            checkboxPosition: 'left',
            multiple: true,
            allowCustomValuesByEnter: true,
          },
        },
        utils: {
          tags: { labelFormatter: capitalize },
        },
        searchKey: 'value',
        config: { labelKey: 'value', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.DEGREE}`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        exactMatch: {
          enabled: true,
          default: SearchMatchType.SMART,
        },
        field: 'exclude_education_degree',
        key: 'exclude_education_degree',
        layer: 4,
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Exclude Degree',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            allowCustomValuesByEnter: true,
          },
        },
        utils: {
          tags: { labelFormatter: capitalize },
        },
        searchKey: 'value',
        config: { labelKey: 'value', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.DEGREE}`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        exactMatch: {
          enabled: true,
          default: SearchMatchType.SMART,
        },
        field: 'include_all_of_education_degree',
        key: 'include_all_of_education_degree',
        layer: 4,
        metadata: {
          color: 'warning',
          icon: 'add-task',
          preset: 'industry',
          placeholder: 'Include Degree (AND)',
          autocomplete: {
            checkboxPosition: 'left',
            multiple: true,
            allowCustomValuesByEnter: true,
          },
        },
        utils: {
          tags: { labelFormatter: capitalize },
        },
        searchKey: 'value',
        config: { labelKey: 'value', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.DEGREE}`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
    ],
  };

  const fieldOfStudyFilter: SearchFilter = {
    name: 'Major',
    type: SearchFilterType.NONE,
    icon: undefined,
    index: 2,
    key: 'major_field_of_study',
    field: 'major_field_of_study',
    metadata: { color: 'primary' },
    config: { labelKey: 'label', valueKey: 'value' },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Major/Field of Study',
        },
      },
    },
    open: false,
    sections: { main: 3 },
    layer: 4,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        exactMatch: {
          enabled: true,
          default: SearchMatchType.SMART,
        },
        field: 'include_education_major',
        key: 'include_education_major',
        layer: 4,
        metadata: {
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Include Major',
          autocomplete: {
            checkboxPosition: 'left',
            multiple: true,
            allowCustomValuesByEnter: true,
          },
        },
        utils: {
          tags: { labelFormatter: capitalize },
        },
        searchKey: 'value',
        config: { labelKey: 'value', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.FIELD_OF_STUDY}`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        exactMatch: {
          enabled: true,
          default: SearchMatchType.SMART,
        },
        field: 'exclude_education_major',
        key: 'exclude_education_major',
        layer: 4,
        metadata: {
          color: 'error',
          icon: 'block',
          preset: 'industry',
          placeholder: 'Exclude Major',
          autocomplete: {
            checkboxPosition: 'left',
            multiple: true,
            allowCustomValuesByEnter: true,
          },
        },
        utils: {
          tags: { labelFormatter: capitalize },
        },
        searchKey: 'value',
        config: { labelKey: 'value', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.FIELD_OF_STUDY}`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        exactMatch: {
          enabled: true,
          default: SearchMatchType.SMART,
        },
        field: 'include_all_of_education_major',
        key: 'include_all_of_education_major',
        layer: 4,
        metadata: {
          color: 'warning',
          icon: 'add-task',
          preset: 'industry',
          placeholder: 'Include Major (AND)',
          autocomplete: {
            checkboxPosition: 'left',
            multiple: true,
            allowCustomValuesByEnter: true,
          },
        },
        utils: {
          tags: { labelFormatter: capitalize },
        },
        searchKey: 'value',
        config: { labelKey: 'value', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.FIELD_OF_STUDY}`,
            label: '',
            icon: null,
            index: undefined,
          },
        ],
      },
    ],
  };

  const yearRangeFilter: SearchFilter = {
    name: 'Year Range',
    type: SearchFilterType.NONE,
    icon: undefined,
    index: 3,
    key: 'company_type_exclude_accordion',
    field: 'company_type_exclude_accordion',
    metadata: { color: 'primary' },
    config: { labelKey: 'label', valueKey: 'value' },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Year Range',
        },
      },
    },
    open: false,
    layer: 4,
    items: [
      {
        name: 'Year Range',
        type: SearchFilterType.NUMBER_RANGE,
        index: 0,
        key: 'education_year',
        field: 'education_year',
        metadata: {
          color: 'primary',
          numberRange: {
            switch: {
              enabled: true,
              title: 'Include Present',
            },
            startPlaceholder: 'Start Year',
            endPlaceholder: 'End Year',
          },
        },
        options: generateEducationFilterYearOptions(),
        nested: false,
      },
    ],
  };

  return {
    name: 'Education',
    icon: 'menu-book',
    index: 10,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.CONTACT_DATA],
    pinned: true,
    sections: { main: 1, more: 1 },
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        metadata: { allowParallelItems: true },
        config: { labelKey: 'label', valueKey: 'value' },
        items: [
          ...[
            {
              name: 'Applied Filters',
              type: SearchFilterType.APPLIED_FILTERS,
              index: 2,
              icon: undefined,
              key: 'education_applied_filters',
              field: 'education_applied_filters',
              metadata: {},
            },
          ],
          schoolFilter,
          degreeFilter,
          fieldOfStudyFilter,
        ],
      },
      yearRangeFilter,
    ],
  };
}
